import { ESymbolCategory } from "@kuika/kuika-cl-model";
import { CascaderOptionType } from "antd/es/cascader";
import React from "react";
import { KuikaTag } from "../../components/ui-kit/kuika-tag/kuika-tag";
import { highlightCascaderKeyword, matchesCascaderSearch, normalizeCascaderSearchText } from "./cascader-search";
import { getDesignerSymbolPickerOptionName } from "./designer-symbol-picker-engine";

/**
 * The symbol picker cascader's four `showSearch` hooks (KOA-18235). Out of the component because each
 * is a pure function of one path.
 */

export const SEARCH_PATH_SEPARATOR: string = " / ";

/** Always-kept leading segments: the category group and what sits under it. */
const SEARCH_PATH_HEAD_SEGMENTS: number = 2;

/** Below this depth a row is shown whole — covers "Action Results / act / First / Field / To String". */
const SEARCH_PATH_MAX_SEGMENTS: number = 5;

/**
 * antd's default is 50, filled in flatten order and stopped BEFORE `sort` runs, so on a screen with
 * many actions the searched-for field was often never scanned. No new worst case: with no match the
 * filter already walks the whole tree.
 */
export const SEARCH_MATCH_LIMIT: number = 300;

// Lower sorts first.
const RELEVANCE_VALUE_PREFIX: number = 0;
const RELEVANCE_VALUE_CONTAINS: number = 1;
const RELEVANCE_ANCESTOR_PREFIX: number = 2;
const RELEVANCE_ANCESTOR_CONTAINS: number = 3;
const RELEVANCE_FUZZY_ONLY: number = 4;

/** Levels that only hold the converters listed beneath them; the converter itself says as much. */
const GROUPING_CATEGORIES: ReadonlySet<ESymbolCategory> = new Set<ESymbolCategory>([
  ESymbolCategory.typeConvertions,
  ESymbolCategory.cardinalityConvertions
]);

/** "First"/"Last"/".selected", "To String" — qualifiers on the value, not the value. */
const MODIFIER_CATEGORIES: ReadonlySet<ESymbolCategory> = new Set<ESymbolCategory>([
  ESymbolCategory.cardinalityConvertion,
  ESymbolCategory.typeConvertion
]);

export interface ISymbolPickerPathSegment {
  name: string;
  isModifier: boolean;
  /** "DateTimeOffset", "List of Dokuman" — what `generateItemLabel` draws under the name. */
  typeCaption?: string;
  /** The action's tag, drawn as a chip beside an action-result name. */
  tag?: string;
}

/** Marks the elided middle of a collapsed path. */
export const COLLAPSED_PATH_SEGMENT: null = null;

/**
 * Names and classifies each level, dropping the pure grouping ones.
 *
 * READ-ONLY. It used to write the flattened name back onto the option — `option.label = name` — and
 * that survived the search: antd hands this the live option objects, so every path a query touched
 * lost the label `generateItemLabel` built for it and came back from the search as a bare name.
 * "Search once and the types and tags disappear from browsing" was that line (KOA-18357). The
 * write-back had no remaining reader: this popup renders search rows itself from these segments,
 * `getDesignerSymbolPickerOptionName` reads the element's `key` rather than needing a string, and
 * `buildSymbolFromSelectedOptions` goes through `getDesignerSymbolPickerOptionLabel`, which flattens
 * a ReactNode (KOA-18185).
 */
export function describeSymbolPickerPath(path: CascaderOptionType[]): ISymbolPickerPathSegment[] {
  const segments: ISymbolPickerPathSegment[] = [];

  for (const option of path) {
    const name: string = getDesignerSymbolPickerOptionName(option);

    const category: ESymbolCategory | undefined = typeof option.category === "number" ? option.category : undefined;
    if (category !== undefined && GROUPING_CATEGORIES.has(category)) {
      continue;
    }
    segments.push({
      name,
      isModifier: category !== undefined && MODIFIER_CATEGORIES.has(category),
      typeCaption: typeof option.typeCaption === "string" ? option.typeCaption : undefined,
      tag: typeof option.actionTag === "string" && option.actionTag !== "" ? option.actionTag : undefined
    });
  }

  return segments;
}

/**
 * The value being picked: the last NON-modifier level. An action-result path ends in a converter
 * (`… / CreatedAt / Conversion / To String`), so keying on `length - 1` kept "To String" and elided
 * the field — the only segment telling two rows apart.
 */
export function getSymbolPickerValueIndex(segments: ISymbolPickerPathSegment[]): number {
  for (let index: number = segments.length - 1; index >= 0; index -= 1) {
    if (!segments[index].isModifier) {
      return index;
    }
  }
  return segments.length - 1;
}

/**
 * Matched against the joined path, so a field is reachable by its own name, by its action's, or by
 * both at once — the separators are in the haystack, so a query may span levels.
 */
export function matchSymbolPickerPath(searchText: string, path: CascaderOptionType[]): boolean {
  const needle: string = normalizeCascaderSearchText(searchText);
  if (!needle) {
    // Before flattening: browse columns must keep the engine's rich ReactNode labels.
    return true;
  }

  const segments: ISymbolPickerPathSegment[] = describeSymbolPickerPath(path);
  return matchesCascaderSearch(normalizeCascaderSearchText(joinSegments(segments)), needle);
}

/** `needle` must already be normalized. */
export function getSymbolPickerPathRelevance(path: CascaderOptionType[], needle: string): number {
  const segments: ISymbolPickerPathSegment[] = describeSymbolPickerPath(path);
  const names: string[] = segments.map((segment: ISymbolPickerPathSegment): string =>
    normalizeCascaderSearchText(segment.name)
  );

  const valueIndex: number = getSymbolPickerValueIndex(segments);
  const value: string = names[valueIndex] ?? "";
  if (value.startsWith(needle)) {
    return RELEVANCE_VALUE_PREFIX;
  }
  if (value.includes(needle)) {
    return RELEVANCE_VALUE_CONTAINS;
  }

  const others: string[] = names.filter((_name: string, index: number): boolean => index !== valueIndex);
  if (others.some((name: string): boolean => name.startsWith(needle))) {
    return RELEVANCE_ANCESTOR_PREFIX;
  }
  if (others.some((name: string): boolean => name.includes(needle))) {
    return RELEVANCE_ANCESTOR_CONTAINS;
  }
  return RELEVANCE_FUZZY_ONLY;
}

export function compareSymbolPickerPaths(a: CascaderOptionType[], b: CascaderOptionType[], searchText: string): number {
  const needle: string = normalizeCascaderSearchText(searchText);
  const relevance: number = getSymbolPickerPathRelevance(a, needle) - getSymbolPickerPathRelevance(b, needle);
  if (relevance !== 0) {
    return relevance;
  }
  // Alphabetical, which also keeps one action's fields adjacent.
  const pathA: string = joinSegments(describeSymbolPickerPath(a));
  const pathB: string = joinSegments(describeSymbolPickerPath(b));
  return pathA.localeCompare(pathB, undefined, { sensitivity: "base" });
}

/**
 * Bounded by the value's index rather than a fixed offset from the end, so the category, the value and
 * any converter after it always survive and a row never grows with the type's nesting depth.
 */
export function collapseSymbolPickerPath(segments: ISymbolPickerPathSegment[]): (ISymbolPickerPathSegment | null)[] {
  if (segments.length <= SEARCH_PATH_MAX_SEGMENTS) {
    return segments;
  }
  const tailStart: number = Math.max(getSymbolPickerValueIndex(segments), SEARCH_PATH_HEAD_SEGMENTS);
  if (tailStart <= SEARCH_PATH_HEAD_SEGMENTS) {
    return segments;
  }
  return [...segments.slice(0, SEARCH_PATH_HEAD_SEGMENTS), COLLAPSED_PATH_SEGMENT, ...segments.slice(tailStart)];
}

/**
 * antd's default renderer emits one flat string that `.cascaderActionPicker`'s 300px nowrap clamp then
 * truncates from the right — cutting off the field name. `kuika-symbol-search-result` is the marker
 * styling.scss keys its `:has()` exemption on (same escape hatch as the action picker, KOA-18068);
 * removing the span silently restores the truncation.
 */
export function renderSymbolPickerSearchRow(
  searchText: string,
  path: CascaderOptionType[],
  prefixCls: string | undefined
): React.ReactNode {
  const segments: ISymbolPickerPathSegment[] = describeSymbolPickerPath(path);
  // Identity, not name: two levels can share a name.
  const valueSegment: ISymbolPickerPathSegment | undefined = segments[getSymbolPickerValueIndex(segments)];
  const shown: (ISymbolPickerPathSegment | null)[] = collapseSymbolPickerPath(segments);
  // The WHOLE path, uncollapsed and without the type caption or the action tag: the row's identity,
  // for the tooltip and for the test-id. A row is what antd renders it as — the visible text now
  // carries a trailing "Boolean" and can elide its middle to "…", so neither the accessible name nor
  // the rendered string identifies a row any more. `symbolSearchResult-<path>` mirrors
  // `symbolPickerOption-<label>` on the browse rows (symbol-picker-cascader.tsx), which is the hook
  // the E2E suite already addresses this picker by.
  const fullPath: string = joinSegments(segments);

  return (
    <span className="kuika-symbol-search-result" data-testid={`symbolSearchResult-${fullPath}`} title={fullPath}>
      {shown.map((segment: ISymbolPickerPathSegment | null, index: number) => (
        <React.Fragment key={`${index}_${segment?.name ?? "collapsed"}`}>
          {index > 0 && <span className="kuika-symbol-search-result__separator">{SEARCH_PATH_SEPARATOR}</span>}
          {segment === COLLAPSED_PATH_SEGMENT ? (
            <span className="kuika-symbol-search-result__separator">…</span>
          ) : (
            <span
              className={
                segment === valueSegment ? "kuika-symbol-search-result__value" : "kuika-symbol-search-result__context"
              }
            >
              {highlightCascaderKeyword(segment.name, searchText, prefixCls)}
            </span>
          )}
          {/* The tag goes wherever it belongs — it hangs off the ACTION level, which in a result is
              one of the context segments, so pinning it to the value hid it entirely. It is also
              the one thing that separates two runs of the same action, which is exactly what a
              breadcrumb is for. The type stays on the value: it describes that segment, and one per
              level would bury the one that matters. */}
          {segment?.tag && <KuikaTag type="action" label={segment.tag} style={{ marginLeft: 4 }} />}
          {segment === valueSegment && segment?.typeCaption && (
            <span className="kuika-symbol-search-result__type">{segment.typeCaption}</span>
          )}
        </React.Fragment>
      ))}
    </span>
  );
}

function joinSegments(segments: ISymbolPickerPathSegment[]): string {
  return segments.map((segment: ISymbolPickerPathSegment): string => segment.name).join(SEARCH_PATH_SEPARATOR);
}
